import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  InputNumber,
  Modal,
  Progress,
  Tag,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { orpcClient } from "@/lib/orpc-client";
import { useFeature } from "@/config/useFeature";
import {
  GENERAL_POOL,
  MAX_SEATS,
  TAX_RATE,
  allocationChanges,
  buildOrder,
  canAllocate,
  clearPendingSeatOrder,
  getPendingSeatOrder,
  getSubscription,
  isSeatOrderFulfilled,
  savePendingSeatOrder,
  setSubscription,
  unitPriceForPool,
  type Allocation,
  type OrderSummary,
  type SeatOrder,
  type SeatPoolRow,
  type SeatTotals,
  type Subscription,
} from "@/lib/licenseQuota/api";

export type RoleQuota = {
  role: string;
  allowedCount: number;
  currentCount: number;
};

const PRIMARY = "#1677ff";
const DANGER = "#ff4d4f";
const MUTED = "#8c8c8c";
const BORDER = "#f0f0f0";

const formatMoney = (n: number) => `NT$ ${n.toLocaleString("en-US")}`;
const formatSignedMoney = (n: number) =>
  n === 0 ? formatMoney(0) : `${n > 0 ? "+" : "−"}${formatMoney(Math.abs(n))}`;
const formatDelta = (n: number) =>
  n === 0 ? "0" : `${n > 0 ? "+" : "−"}${Math.abs(n)}`;

function SubscriptionCard() {
  const { t } = useTranslation();
  const [subscription, setSub] = useState<Subscription | null>(null);
  const [editing, setEditing] = useState(false);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  useEffect(() => {
    getSubscription().then(setSub);
  }, []);

  const openEditor = () => {
    setRange(
      subscription
        ? [dayjs(subscription.startDate), dayjs(subscription.endDate)]
        : null
    );
    setEditing(true);
  };

  const save = async () => {
    if (!range) return;
    const next = {
      startDate: range[0].format("YYYY-MM-DD"),
      endDate: range[1].format("YYYY-MM-DD"),
    };
    await setSubscription(next);
    setSub(next);
    setEditing(false);
  };

  const start = subscription ? dayjs(subscription.startDate) : null;
  const end = subscription ? dayjs(subscription.endDate) : null;
  const today = dayjs().startOf("day");
  const totalDays = start && end ? Math.max(end.diff(start, "day"), 1) : 0;
  const elapsedDays = start ? today.diff(start, "day") : 0;
  const percent = subscription
    ? Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100)
    : 0;
  const daysLeft = end ? end.diff(today, "day") : 0;
  const expired = !!subscription && daysLeft < 0;

  return (
    <Card size="small" data-testid="subscription-card">
      <div className="flex items-center justify-between mb-2">
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {t("employee.page.subscriptionTitle")}
        </span>
        <Button
          size="middle"
          icon={<EditOutlined />}
          onClick={openEditor}
          aria-label={t("employee.page.subscriptionEdit")}
        >
          {t("employee.page.subscriptionEdit")}
        </Button>
      </div>
      {subscription ? (
        <>
          <div
            className="flex justify-between flex-wrap gap-2"
            style={{ fontSize: 13, marginBottom: 4 }}
          >
            <span style={{ color: "#999" }}>
              {subscription.startDate} ~ {subscription.endDate}
            </span>
            <span
              style={{ fontWeight: 500, color: expired ? DANGER : undefined }}
              data-testid="subscription-days-left"
            >
              {expired
                ? t("employee.page.subscriptionExpired")
                : t("employee.page.subscriptionDaysLeft", { days: daysLeft })}
            </span>
          </div>
          <Progress
            percent={Math.round(percent)}
            showInfo={false}
            strokeColor={
              expired || daysLeft <= 30
                ? DANGER
                : daysLeft <= 90
                  ? "#faad14"
                  : PRIMARY
            }
          />
        </>
      ) : (
        <div style={{ fontSize: 13, color: "#999" }}>
          {t("employee.page.subscriptionNotSet")}
        </div>
      )}
      <Modal
        open={editing}
        title={t("employee.page.subscriptionTitle")}
        onCancel={() => setEditing(false)}
        onOk={save}
        okButtonProps={{ disabled: !range }}
        okText={t("employee.page.confirm")}
        cancelText={t("employee.page.cancel")}
        destroyOnHidden
      >
        <div style={{ fontSize: 13, color: "#999", marginBottom: 8 }}>
          {t("employee.page.subscriptionPeriod")}
        </div>
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          value={range}
          onChange={(v) =>
            setRange(v && v[0] && v[1] ? [v[0], v[1]] : null)
          }
          inputReadOnly
        />
      </Modal>
    </Card>
  );
}

function SeatStepper({
  value,
  onChange,
  label,
  min = 0,
  max = MAX_SEATS,
  changed = false,
  testId,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  min?: number;
  max?: number;
  changed?: boolean;
  testId: string;
}) {
  const { t } = useTranslation();
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.floor(n)));
  return (
    <div
      className="flex items-center rounded-md"
      style={{
        border: `1px solid ${changed ? PRIMARY : "#d9d9d9"}`,
        background: "#fff",
      }}
      data-testid={testId}
    >
      <Button
        type="text"
        icon={<MinusOutlined />}
        disabled={value <= min}
        onClick={() => onChange(clamp(value - 1))}
        aria-label={t("employee.page.seat.decrease", { label })}
        style={{ width: 40, height: 40 }}
      />
      <InputNumber
        variant="borderless"
        controls={false}
        precision={0}
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(v) => {
          if (typeof v === "number" && !Number.isNaN(v)) onChange(clamp(v));
        }}
        aria-label={t("employee.page.seat.inputLabel", { label })}
        style={{
          width: 56,
          fontWeight: 500,
          color: changed ? PRIMARY : undefined,
        }}
        className="seat-stepper-input"
      />
      <Button
        type="text"
        icon={<PlusOutlined />}
        disabled={value >= max}
        onClick={() => onChange(clamp(value + 1))}
        aria-label={t("employee.page.seat.increase", { label })}
        style={{ width: 40, height: 40 }}
      />
    </div>
  );
}

function UsageBar({ used, total }: { used: number; total: number }) {
  const percent = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  return (
    <Progress
      percent={Math.max(percent, used > 0 ? 1.5 : 0)}
      showInfo={false}
      size="small"
      strokeColor={used > total ? DANGER : PRIMARY}
    />
  );
}

function SeatCard({
  role,
  used,
  original,
  value,
  onChange,
  unitPrice,
}: {
  role: string;
  used: number;
  original: number;
  value: number;
  onChange: (value: number) => void;
  unitPrice?: number;
}) {
  const { t } = useTranslation();
  const name = t(`employee.page.roles.${role}.name`, role);
  const desc = t(`employee.page.roles.${role}.description`, "");
  const delta = value - original;
  const changed = delta !== 0;
  const available = value - used;
  const min = used;

  return (
    <div
      className="flex flex-col rounded-xl bg-white"
      style={{ border: `1px solid ${changed ? PRIMARY : "#e5e5e5"}` }}
      data-testid={`seat-card-${role}`}
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span style={{ fontWeight: 700, fontSize: 16 }}>{name}</span>
            <span style={{ fontSize: 13, color: MUTED }}>{desc}</span>
          </div>
          <Tag
            color={available > 0 ? "default" : "error"}
            style={{ marginInlineEnd: 0, fontSize: 13, padding: "2px 8px" }}
            data-testid={`seat-available-${role}`}
          >
            {available > 0
              ? `${available} ${t("employee.page.available")}`
              : t("employee.page.depleted")}
          </Tag>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between" style={{ fontSize: 13 }}>
            <span style={{ color: MUTED }}>{t("employee.page.usageStatus")}</span>
            <span style={{ fontWeight: 500 }} data-testid={`seat-usage-${role}`}>
              {used} /{" "}
              {changed ? (
                <>
                  <span style={{ color: MUTED, textDecoration: "line-through" }}>
                    {original}
                  </span>{" "}
                  <span style={{ color: PRIMARY }}>{value}</span>
                </>
              ) : (
                original
              )}
            </span>
          </div>
          <UsageBar used={used} total={value} />
        </div>
      </div>
      <div
        className="mt-auto flex items-center justify-between gap-3 px-4 py-3 rounded-b-xl"
        style={{ borderTop: `1px solid ${BORDER}`, background: "#fafafa" }}
      >
        <div className="flex flex-col">
          <span style={{ fontSize: 12, color: MUTED }}>
            {unitPrice === undefined
              ? t("employee.page.seat.allocatedSeats")
              : t("employee.page.seat.seatCount")}
            {changed && (
              <span style={{ marginLeft: 6, fontWeight: 500, color: PRIMARY }}>
                ({formatDelta(delta)})
              </span>
            )}
          </span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {unitPrice === undefined ? (
              <>
                {t("employee.page.seat.originalSeats", { n: original })}
                {min > 0 && (
                  <span style={{ fontWeight: 400, color: MUTED }}>
                    ・{t("employee.page.seat.minimum", { n: min })}
                  </span>
                )}
              </>
            ) : (
              <>
                {formatMoney(unitPrice)}
                <span style={{ fontWeight: 400, color: MUTED }}>
                  {" "}
                  {t("employee.page.seat.perSeatMonth")}
                </span>
              </>
            )}
          </span>
        </div>
        <SeatStepper
          value={value}
          onChange={onChange}
          label={name}
          min={min}
          changed={changed}
          testId={`seat-stepper-${role}`}
        />
      </div>
    </div>
  );
}

function SharedPoolHeader({
  total,
  currentTotal,
  allocated,
  minimum,
  onTotalChange,
}: {
  total: number;
  currentTotal: number;
  allocated: number;
  minimum: number;
  onTotalChange: (value: number) => void;
}) {
  const { t } = useTranslation();
  const unassigned = total - allocated;
  const delta = total - currentTotal;
  const percent = total === 0 ? 0 : Math.min(100, (allocated / total) * 100);
  const poolName = t("employee.page.seat.pools.general.name");

  return (
    <div
      className="flex flex-col gap-4 rounded-xl p-4 lg:flex-row lg:items-center lg:justify-between"
      style={{ border: "1px solid #e5e5e5", background: "#f7f9fc" }}
      data-testid="shared-pool-header"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex shrink-0 items-center justify-center rounded-lg"
          style={{ width: 40, height: 40, background: "#e6f4ff", color: PRIMARY, fontSize: 18 }}
        >
          <SwapOutlined />
        </div>
        <div className="flex flex-col gap-1">
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            {poolName}
            {t("employee.page.seat.interchangeable")}
          </span>
          <span style={{ fontSize: 13, color: MUTED }}>
            {t("employee.page.seat.pools.general.description")}
          </span>
          <span style={{ fontSize: 13 }}>
            <span style={{ fontWeight: 500 }}>
              {formatMoney(unitPriceForPool(GENERAL_POOL))}
            </span>
            <span style={{ color: MUTED }}> {t("employee.page.seat.perSeatMonth")}</span>
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end lg:shrink-0">
        <div className="flex flex-col gap-1" style={{ minWidth: 224 }}>
          <div className="flex items-center justify-between" style={{ fontSize: 13 }}>
            <span style={{ color: MUTED }}>{t("employee.page.seat.allocated")}</span>
            <span style={{ fontWeight: 500 }} data-testid="pool-allocated">
              {allocated} / {total}
            </span>
          </div>
          <Progress
            percent={percent}
            showInfo={false}
            size="small"
            strokeColor={unassigned < 0 ? DANGER : PRIMARY}
          />
          <span
            aria-live="polite"
            data-testid="pool-unassigned"
            style={{
              fontSize: 12,
              color: unassigned === 0 ? MUTED : unassigned > 0 ? PRIMARY : DANGER,
            }}
          >
            {unassigned === 0 ? (
              <>
                <CheckCircleOutlined /> {t("employee.page.seat.fullyAllocated")}
              </>
            ) : (
              <>
                <ExclamationCircleOutlined />{" "}
                {unassigned > 0
                  ? t("employee.page.seat.unassigned", { n: unassigned })
                  : t("employee.page.seat.overAllocated", { n: -unassigned })}
              </>
            )}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span style={{ fontSize: 12, color: MUTED }}>
            {t("employee.page.seat.totalSeats")}
            {delta !== 0 && (
              <span style={{ marginLeft: 6, fontWeight: 500, color: PRIMARY }}>
                ({formatDelta(delta)})
              </span>
            )}
          </span>
          <SeatStepper
            value={total}
            onChange={onTotalChange}
            label={poolName}
            min={minimum}
            changed={delta !== 0}
            testId="seat-stepper-pool-general"
          />
        </div>
      </div>
    </div>
  );
}

function CheckoutBar({
  order,
  seatsChanged,
  unassigned,
  valid,
  saving,
  onReset,
  onSave,
  onCheckout,
}: {
  order: OrderSummary;
  seatsChanged: boolean;
  unassigned: number;
  valid: boolean;
  saving: boolean;
  onReset: () => void;
  onSave: () => void;
  onCheckout: () => void;
}) {
  const { t } = useTranslation();
  const changedLines = order.lines.filter((l) => l.deltaSeats !== 0);
  const poolName = (pool: string) =>
    t(`employee.page.seat.pools.${pool}.name`, pool);

  return (
    <div
      className="sticky bottom-4 z-10 flex flex-col gap-4 rounded-xl bg-white p-4 lg:flex-row lg:items-center lg:justify-between"
      style={{
        border: "1px solid #91caff",
        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
      }}
      data-testid="checkout-bar"
    >
      <div className="flex items-center gap-3">
        <div
          className="flex shrink-0 items-center justify-center rounded-lg"
          style={{ width: 40, height: 40, background: "#e6f4ff", color: PRIMARY, fontSize: 18 }}
        >
          <ShoppingCartOutlined />
        </div>
        <div className="flex flex-col gap-0.5">
          <span style={{ fontSize: 14 }}>
            {t("employee.page.seat.totalAfter", { n: order.totalSeats })}
          </span>
          {unassigned !== 0 ? (
            <span
              role="status"
              data-testid="checkout-bar-notice"
              style={{ fontSize: 12, color: valid ? PRIMARY : DANGER }}
            >
              <ExclamationCircleOutlined />{" "}
              {unassigned > 0
                ? t("employee.page.seat.unassignedNotice", { n: unassigned })
                : t("employee.page.seat.overNotice", { n: -unassigned })}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: MUTED }} data-testid="checkout-bar-summary">
              {changedLines.length > 0
                ? changedLines
                    .map((l) => `${poolName(l.pool)} ${l.currentSeats} → ${l.newSeats}`)
                    .join("・")
                : t("employee.page.seat.allocationOnly")}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 lg:justify-end">
        <div className="flex flex-col lg:items-end">
          <span style={{ fontSize: 12, color: MUTED }}>
            {t("employee.page.seat.monthlyAfter")}
          </span>
          <span style={{ fontSize: 20, fontWeight: 700 }} data-testid="checkout-bar-total">
            {formatMoney(order.total)}
          </span>
          <span
            style={{ fontSize: 12, color: order.difference === 0 ? MUTED : PRIMARY }}
            data-testid="checkout-bar-difference"
          >
            {t("employee.page.seat.vsCurrent", {
              amount: formatSignedMoney(order.difference),
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button type="text" size="large" onClick={onReset}>
            {t("employee.page.seat.reset")}
          </Button>
          {seatsChanged ? (
            <Button type="primary" size="large" disabled={!valid} onClick={onCheckout}>
              {t("employee.page.seat.checkout")}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              disabled={!valid}
              loading={saving}
              onClick={onSave}
            >
              {t("employee.page.seat.saveAllocation")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckoutDialog({
  open,
  order,
  changes,
  baseAllocation,
  unassigned,
  onClose,
  onConfirm,
}: {
  open: boolean;
  order: OrderSummary;
  changes: { role: string; allowedCount: number }[];
  baseAllocation: Allocation;
  unassigned: number;
  onClose: () => void;
  onConfirm: () => Promise<{ saved: SeatOrder; deferred: boolean }>;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "submitting" | "created">("idle");
  const [result, setResult] = useState<{ saved: SeatOrder; deferred: boolean } | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (status === "submitting") return;
    setStatus("idle");
    setResult(null);
    setError(null);
    onClose();
  };

  const confirm = async () => {
    setStatus("submitting");
    setError(null);
    try {
      setResult(await onConfirm());
      setStatus("created");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("employee.page.seat.saveFailed"));
      setStatus("idle");
    }
  };

  const poolName = (pool: string) =>
    t(`employee.page.seat.pools.${pool}.name`, pool);

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      destroyOnHidden
      title={status === "created" ? null : t("employee.page.seat.confirmTitle")}
      width={520}
    >
      {status === "created" && result ? (
        <div
          className="flex flex-col items-center gap-3 py-4 text-center"
          data-testid="checkout-created"
        >
          <CheckCircleOutlined style={{ fontSize: 40, color: PRIMARY }} />
          <span style={{ fontSize: 18, fontWeight: 700 }}>
            {t("employee.page.seat.orderCreated")}
          </span>
          <span style={{ color: MUTED }}>
            {t("employee.page.seat.orderId")}{" "}
            <span style={{ fontFamily: "monospace", color: "#000" }} data-testid="checkout-order-id">
              {result.saved.id}
            </span>
          </span>
          <span style={{ color: MUTED }}>
            {t("employee.page.seat.orderCreatedDesc", {
              amount: formatMoney(result.saved.total),
            })}
          </span>
          {result.deferred && (
            <span style={{ color: PRIMARY, fontSize: 13 }} data-testid="checkout-deferred">
              {t("employee.page.seat.allocationDeferred")}
            </span>
          )}
          <Button type="primary" size="large" block onClick={close}>
            {t("employee.page.seat.done")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <span style={{ color: MUTED }}>{t("employee.page.seat.confirmDesc")}</span>
          <div className="flex flex-col rounded-lg" style={{ border: `1px solid ${BORDER}` }}>
            <div
              className="grid gap-x-4 px-4 py-2"
              style={{
                gridTemplateColumns: "1fr auto 96px",
                fontSize: 12,
                color: MUTED,
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <span>{t("employee.page.seat.plan")}</span>
              <span className="text-right">{t("employee.page.seat.seats")}</span>
              <span className="text-right">{t("employee.page.seat.monthlyAmount")}</span>
            </div>
            {order.lines.map((l) => (
              <div
                key={l.pool}
                className="grid items-center gap-x-4 px-4 py-3"
                style={{
                  gridTemplateColumns: "1fr auto 96px",
                  borderBottom: `1px solid ${BORDER}`,
                }}
                data-testid={`checkout-line-${l.pool}`}
              >
                <div className="flex flex-col">
                  <span style={{ fontWeight: 500 }}>{poolName(l.pool)}</span>
                  <span style={{ fontSize: 12, color: MUTED }}>
                    {formatMoney(l.unitPrice)} {t("employee.page.seat.perSeatMonth")}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span>{l.newSeats}</span>
                  <span
                    style={{ fontSize: 12, color: l.deltaSeats === 0 ? MUTED : PRIMARY }}
                  >
                    {l.deltaSeats === 0
                      ? t("employee.page.seat.unchanged")
                      : t("employee.page.seat.originalDelta", {
                          current: l.currentSeats,
                          delta: formatDelta(l.deltaSeats),
                        })}
                  </span>
                </div>
                <span className="text-right" style={{ fontWeight: 500 }}>
                  {formatMoney(l.amount)}
                </span>
              </div>
            ))}
          </div>

          {changes.length > 0 && (
            <div className="flex flex-col gap-2">
              <span style={{ fontSize: 12, color: MUTED }}>
                {t("employee.page.seat.allocationChanges")}
              </span>
              <div className="flex flex-wrap gap-2">
                {changes.map((c) => (
                  <span
                    key={c.role}
                    className="rounded-md px-2.5 py-1"
                    style={{ border: `1px solid ${BORDER}`, fontSize: 12 }}
                  >
                    {t(`employee.page.roles.${c.role}.name`, c.role)}{" "}
                    {baseAllocation[c.role] ?? 0} →{" "}
                    <span style={{ color: PRIMARY }}>{c.allowedCount}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2" style={{ fontSize: 14 }}>
            <div className="flex justify-between">
              <span style={{ color: MUTED }}>
                {t("employee.page.seat.subtotalSeats", { n: order.totalSeats })}
              </span>
              <span data-testid="checkout-subtotal">{formatMoney(order.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: MUTED }}>
                {t("employee.page.seat.tax", { rate: Math.round(TAX_RATE * 100) })}
              </span>
              <span data-testid="checkout-tax">{formatMoney(order.tax)}</span>
            </div>
            <div
              className="flex items-baseline justify-between pt-3"
              style={{ borderTop: `1px solid ${BORDER}` }}
            >
              <span style={{ fontWeight: 500 }}>{t("employee.page.seat.totalMonthly")}</span>
              <span
                style={{ fontSize: 22, fontWeight: 700, color: PRIMARY }}
                data-testid="checkout-total"
              >
                {formatMoney(order.total)}
              </span>
            </div>
            <div className="flex justify-between" style={{ fontSize: 12, color: MUTED }}>
              <span>
                {t("employee.page.seat.currentMonthly", {
                  amount: formatMoney(order.currentTotal),
                })}
              </span>
              <span>
                {t("employee.page.seat.difference", {
                  amount: formatSignedMoney(order.difference),
                })}
              </span>
            </div>
          </div>

          {unassigned > 0 && (
            <div
              role="status"
              className="flex items-start gap-2 rounded-lg px-3 py-2"
              style={{ background: "#f0f7ff", fontSize: 13 }}
            >
              <InfoCircleOutlined style={{ color: PRIMARY, marginTop: 3 }} />
              {t("employee.page.seat.unassignedCheckout", { n: unassigned })}
            </div>
          )}

          {error && (
            <span role="alert" style={{ color: DANGER, fontSize: 13 }}>
              {error}
            </span>
          )}

          <div className="flex justify-end gap-2">
            <Button size="large" onClick={close} disabled={status === "submitting"}>
              {t("employee.page.seat.back")}
            </Button>
            <Button
              type="primary"
              size="large"
              loading={status === "submitting"}
              onClick={confirm}
            >
              {t("employee.page.seat.confirmCheckout")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PendingOrderBanner({
  order,
  pools,
  onCancel,
}: {
  order: SeatOrder;
  pools: SeatPoolRow[];
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const changed = pools.filter(
    (p) => order.seats[p.pool] !== undefined && order.seats[p.pool] !== p.seatCount
  );
  const deferred = Object.entries(order.deferredAllocation);
  return (
    <div
      className="flex flex-col gap-2 rounded-lg px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      style={{ background: "#e6f4ff", color: "#0958d9", fontSize: 13 }}
      data-testid="pending-order"
    >
      <div className="flex flex-col gap-1">
        <span style={{ fontWeight: 600 }}>
          {t("employee.page.seat.pendingTitle")}{" "}
          <span style={{ color: MUTED, fontWeight: 400 }}>
            ({t("employee.page.pendingNotEffective")})
          </span>
        </span>
        <span data-testid="pending-order-lines">
          {changed
            .map(
              (p) =>
                `${t(`employee.page.seat.pools.${p.pool}.name`, p.pool)} ${p.seatCount} → ${order.seats[p.pool]}`
            )
            .join("・")}
          {" ・ "}
          {t("employee.page.seat.monthlyAfter")} {formatMoney(order.total)}
        </span>
        {deferred.length > 0 && (
          <span>
            {t("employee.page.seat.pendingAllocation")}：
            {deferred
              .map(([role, n]) => `${t(`employee.page.roles.${role}.name`, role)} ${n}`)
              .join("・")}
          </span>
        )}
      </div>
      <Button size="large" onClick={onCancel}>
        {t("employee.page.seat.cancelOrder")}
      </Button>
    </div>
  );
}

const errorMessage = (err: unknown, t: (k: string) => string) => {
  const msg = err instanceof Error ? err.message : "";
  if (msg.includes("Seat allocation exceeded"))
    return t("employee.page.seat.allocationExceeded");
  if (msg.includes("less than used count"))
    return t("employee.page.seat.allocationBelowUsed");
  return t("employee.page.seat.saveFailed");
};

function SeatPoolQuota({
  pools,
  quotas,
  onSaved,
}: {
  pools: SeatPoolRow[];
  quotas: RoleQuota[];
  onSaved: (pools: SeatPoolRow[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const general = pools.find((p) => p.pool === GENERAL_POOL);
  const standalone = pools.filter((p) => p.pool !== GENERAL_POOL);
  const generalRoles = useMemo(() => general?.roles ?? [], [general]);

  const usedOf = useCallback(
    (role: string) => quotas.find((q) => q.role === role)?.currentCount ?? 0,
    [quotas]
  );
  const baseAllocation = useMemo<Allocation>(
    () =>
      Object.fromEntries(
        generalRoles.map((role) => [
          role,
          quotas.find((q) => q.role === role)?.allowedCount ?? 0,
        ])
      ),
    [generalRoles, quotas]
  );
  const baseSeats = useMemo<SeatTotals>(
    () => Object.fromEntries(pools.map((p) => [p.pool, p.seatCount])),
    [pools]
  );

  const [allocation, setAllocation] = useState<Allocation>(baseAllocation);
  const [seats, setSeats] = useState<SeatTotals>(baseSeats);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<SeatOrder | null>(null);

  useEffect(() => setAllocation(baseAllocation), [baseAllocation]);
  useEffect(() => setSeats(baseSeats), [baseSeats]);

  useEffect(() => {
    getPendingSeatOrder().then((o) => {
      if (o && isSeatOrderFulfilled(o, pools)) {
        clearPendingSeatOrder();
        setPendingOrder(null);
      } else {
        setPendingOrder(o);
      }
    });
  }, [pools]);

  const order = useMemo(() => buildOrder(pools, seats), [pools, seats]);
  const changes = useMemo(
    () => allocationChanges(baseAllocation, allocation),
    [baseAllocation, allocation]
  );
  const seatsChanged = order.lines.some((l) => l.deltaSeats !== 0);
  const dirty = seatsChanged || changes.length > 0;
  const generalTotal = seats[GENERAL_POOL] ?? 0;
  const allocated = generalRoles.reduce((s, role) => s + (allocation[role] ?? 0), 0);
  const unassigned = generalTotal - allocated;
  const valid = canAllocate(generalTotal, baseAllocation, allocation);

  const reset = () => {
    setAllocation(baseAllocation);
    setSeats(baseSeats);
  };

  const saveAllocation = async () => {
    setSaving(true);
    try {
      const next = await orpcClient.employee.allocateSeat({ allocations: changes } as any);
      await onSaved(next as SeatPoolRow[]);
      message.success(t("employee.page.seat.allocationSaved"));
    } catch (err) {
      message.error(errorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  const confirmCheckout = async () => {
    const applyNow =
      changes.length > 0 &&
      canAllocate(baseSeats[GENERAL_POOL] ?? 0, baseAllocation, allocation);
    let nextPools: SeatPoolRow[] | null = null;
    if (applyNow) {
      try {
        nextPools = (await orpcClient.employee.allocateSeat({
          allocations: changes,
        } as any)) as SeatPoolRow[];
      } catch (err) {
        throw new Error(errorMessage(err, t));
      }
    }
    const deferredAllocation: Allocation =
      changes.length > 0 && !applyNow
        ? Object.fromEntries(changes.map((c) => [c.role, c.allowedCount]))
        : {};
    const saved = await savePendingSeatOrder(order, deferredAllocation);
    setPendingOrder(saved);
    if (nextPools) await onSaved(nextPools);
    reset();
    return { saved, deferred: Object.keys(deferredAllocation).length > 0 };
  };

  const cancelOrder = async () => {
    await clearPendingSeatOrder();
    setPendingOrder(null);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="seat-pool-quota">
      <div
        className="flex flex-col gap-2 rounded-lg px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
        style={{ background: "#f0f7ff", fontSize: 13 }}
      >
        <span className="flex items-center gap-2">
          <InfoCircleOutlined style={{ color: PRIMARY }} />
          {t("employee.page.seat.pricingNote")}
        </span>
        <span className="flex flex-wrap gap-x-4 gap-y-1" style={{ color: MUTED }}>
          {pools.map((p) => (
            <span key={p.pool} data-testid={`price-${p.pool}`}>
              {t(`employee.page.seat.pools.${p.pool}.name`, p.pool)}{" "}
              <span style={{ fontWeight: 500, color: "#000" }}>
                {formatMoney(unitPriceForPool(p.pool))}
              </span>{" "}
              {t("employee.page.seat.perSeatMonth")}
            </span>
          ))}
        </span>
      </div>

      {pendingOrder && (
        <PendingOrderBanner order={pendingOrder} pools={pools} onCancel={cancelOrder} />
      )}

      {general && (
        <section className="flex flex-col gap-4">
          <SharedPoolHeader
            total={generalTotal}
            currentTotal={general.seatCount}
            allocated={allocated}
            minimum={general.usedCount}
            onTotalChange={(n) => setSeats((prev) => ({ ...prev, [GENERAL_POOL]: n }))}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {generalRoles.map((role) => (
              <SeatCard
                key={role}
                role={role}
                used={usedOf(role)}
                original={baseAllocation[role] ?? 0}
                value={allocation[role] ?? 0}
                onChange={(n) => setAllocation((prev) => ({ ...prev, [role]: n }))}
              />
            ))}
          </div>
        </section>
      )}

      {standalone.length > 0 && (
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span style={{ fontWeight: 700, fontSize: 16 }}>
              {t("employee.page.seat.standaloneTitle")}
            </span>
            <span style={{ fontSize: 13, color: MUTED }}>
              {t("employee.page.seat.standaloneHint")}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {standalone.map((p) => (
              <SeatCard
                key={p.pool}
                role={p.roles[0] ?? p.pool}
                used={p.usedCount}
                original={p.seatCount}
                value={seats[p.pool] ?? p.seatCount}
                onChange={(n) => setSeats((prev) => ({ ...prev, [p.pool]: n }))}
                unitPrice={unitPriceForPool(p.pool)}
              />
            ))}
          </div>
        </section>
      )}

      {dirty && (
        <CheckoutBar
          order={order}
          seatsChanged={seatsChanged}
          unassigned={unassigned}
          valid={valid}
          saving={saving}
          onReset={reset}
          onSave={saveAllocation}
          onCheckout={() => setCheckoutOpen(true)}
        />
      )}

      <CheckoutDialog
        open={checkoutOpen}
        order={order}
        changes={changes}
        baseAllocation={baseAllocation}
        unassigned={unassigned}
        onClose={() => setCheckoutOpen(false)}
        onConfirm={confirmCheckout}
      />
    </div>
  );
}

function RoleQuotaGrid({ roleQuotas }: { roleQuotas: RoleQuota[] }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {roleQuotas.map((q) => {
        const available = q.allowedCount - q.currentCount;
        const usagePercent =
          q.allowedCount > 0 ? (q.currentCount / q.allowedCount) * 100 : 0;
        const roleName = t(`employee.page.roles.${q.role}.name`, q.role);
        const roleDesc = t(`employee.page.roles.${q.role}.description`, "");
        return (
          <Card key={q.role} size="small" data-testid={`quota-card-${q.role}`}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 14 }}>{roleName}</span>
              <Tag
                color={
                  available > 10 ? "default" : available > 0 ? "warning" : "error"
                }
              >
                {available > 0
                  ? `${available} ${t("employee.page.available")}`
                  : t("employee.page.depleted")}
              </Tag>
            </div>
            <div style={{ fontSize: 13, color: "#999", marginBottom: 12 }}>
              {roleDesc}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                marginBottom: 4,
              }}
            >
              <span style={{ color: "#999" }}>{t("employee.page.usageStatus")}</span>
              <span style={{ fontWeight: 500 }}>
                {q.currentCount} / {q.allowedCount}
              </span>
            </div>
            <Progress
              percent={usagePercent}
              showInfo={false}
              strokeColor={
                usagePercent > 90 ? DANGER : usagePercent > 70 ? "#faad14" : PRIMARY
              }
              size="small"
            />
          </Card>
        );
      })}
    </div>
  );
}

function LicensesTab() {
  const seatPoolEnabled = useFeature("licenseQuotaCheckout");
  const [roleQuotas, setRoleQuotas] = useState<RoleQuota[]>([]);
  const [pools, setPools] = useState<SeatPoolRow[] | null>(null);

  const loadQuotas = useCallback(
    () =>
      orpcClient.employee.listRoleQuota({}).then((data) => {
        setRoleQuotas(Array.isArray(data) ? data : []);
      }),
    []
  );

  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  useEffect(() => {
    if (!seatPoolEnabled) return;
    orpcClient.employee
      .listSeatPool({})
      .then((data) => setPools(Array.isArray(data) ? (data as SeatPoolRow[]) : null))
      .catch(() => setPools(null));
  }, [seatPoolEnabled]);

  const onSaved = useCallback(
    async (next: SeatPoolRow[]) => {
      setPools(next);
      await loadQuotas();
    },
    [loadQuotas]
  );

  return (
    <div className="flex flex-col gap-4">
      {seatPoolEnabled && <SubscriptionCard />}
      {seatPoolEnabled && pools ? (
        <SeatPoolQuota pools={pools} quotas={roleQuotas} onSaved={onSaved} />
      ) : (
        <RoleQuotaGrid roleQuotas={roleQuotas} />
      )}
    </div>
  );
}

export default LicensesTab;
